import { createLiquidEngine } from '@payaca/document-rendering/liquid';
import { useThrottledCallback } from '@payaca/hooks';
import { Idiomorph } from 'idiomorph';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// Throttle window for the preview render. The first change fires immediately
// (leading edge) so typing feels responsive; further changes within the window
// coalesce into a single trailing render with the latest value, capping the
// cost of liquid.render + Idiomorph.morph during sustained input bursts.
const RENDER_THROTTLE_MS = 150;

// Builder-only styles that make the preview a navigation surface: the whole
// document is inert and only regions tagged with `data-tab-target` are
// clickable, so a click can select the matching editor tab. Injected here
// rather than baked into the document template — the template renders unchanged
// in the customer-facing document, and is free to change provided it keeps
// emitting the `data-tab-target` attributes.
const INTERACTIVITY_STYLES = `
  :host { pointer-events: none; }
  [data-tab-target] {
    pointer-events: auto;
    cursor: pointer;
    user-select: none;
  }
`;

type UseLivePreviewArgs = {
  template: string;
  context: unknown;
  locale?: string;
  // IANA timezone (e.g. Europe/London) for Liquid's `date` filter, so the
  // preview matches the server-rendered snapshot rather than using the
  // browser's timezone.
  timezone?: string;
  // Called with the `data-tab-target` of the nearest tagged region when the
  // user clicks inside the preview. Lets a click in the rendered document drive
  // the active section tab.
  onTabTargetClick?: (target: string) => void;
  // The currently active `data-tab-target`. When it changes, the matching
  // region is scrolled into view in the preview.
  activeTarget?: string | null;
};

const useLivePreview = (args: UseLivePreviewArgs) => {
  const {
    template,
    context: rawContext,
    locale,
    timezone,
    onTabTargetClick,
    activeTarget,
  } = args;
  const context = useMemo<object>(
    () => (rawContext && typeof rawContext === 'object' ? rawContext : {}),
    [rawContext]
  );

  // The host is tracked in state via a callback ref (not a plain ref) so the
  // effects below re-run when the host element actually mounts. The consumer
  // renders the host conditionally (it shows a loader until the preview loads),
  // so on first render the host is absent — a plain ref wouldn't re-trigger the
  // listener / style-injection effects once it appears.
  const [host, setHost] = useState<HTMLDivElement | null>(null);
  const hostRef = useCallback((node: HTMLDivElement | null) => {
    setHost(node);
  }, []);

  // `outputEscape: 'escape'` makes `{{ var }}` HTML-escape user-supplied
  // values. The preview pane then morphs that output into a Shadow DOM via
  // Idiomorph, which uses innerHTML semantics — without escaping, an
  // attacker-controlled section heading could inject markup like
  // `<img src=x onerror=...>` and execute script.
  //
  // `locale` and `timezone` flow from the account's region preferences so
  // Liquid's `date` filter renders month/day names in the expected language,
  // in the account's timezone rather than the browser's.
  const liquid = useMemo(
    () =>
      createLiquidEngine({
        outputEscape: 'escape',
        locale,
        timezoneOffset: timezone,
      }),
    [locale, timezone]
  );

  const parsedTemplate = useMemo(
    () => liquid.parse(template),
    [liquid, template]
  );

  const renderPreview = useThrottledCallback(() => {
    if (!host) return;
    const shadowRoot = host.shadowRoot ?? host.attachShadow({ mode: 'open' });
    void (async () => {
      // liquidjs types `render` as Promise<any>; normalise to string so the
      // value flows safely into Idiomorph.
      const html = String(await liquid.render(parsedTemplate, context));
      Idiomorph.morph(shadowRoot, html, { morphStyle: 'innerHTML' });
    })();
  }, RENDER_THROTTLE_MS);

  useEffect(() => {
    renderPreview();
  }, [parsedTemplate, context, renderPreview, host]);

  // Wire up the builder interactivity once the host is present and a click
  // handler is provided; until then the preview stays fully selectable.
  //
  // - Styles are injected via `adoptedStyleSheets`, which live on the shadow
  //   root itself, so Idiomorph's innerHTML morph leaves them intact across
  //   re-renders.
  // - The click listener is delegated to the host. The preview content lives in
  //   a Shadow DOM, but clicks are composed events that bubble out to the host,
  //   so `composedPath()` finds the nearest tagged ancestor across the shadow
  //   boundary (`closest()` would fail — the event target is retargeted at the
  //   boundary). The host is stable across morphs, so one listener suffices.
  useEffect(() => {
    if (!host || !onTabTargetClick) return;
    const shadowRoot = host.shadowRoot ?? host.attachShadow({ mode: 'open' });

    const sheet = new CSSStyleSheet();
    sheet.replaceSync(INTERACTIVITY_STYLES);
    shadowRoot.adoptedStyleSheets = [...shadowRoot.adoptedStyleSheets, sheet];

    const handleClick = (event: MouseEvent) => {
      const target = event
        .composedPath()
        .find(
          (node): node is HTMLElement =>
            node instanceof HTMLElement && node.dataset.tabTarget != null
        );
      if (target?.dataset.tabTarget) onTabTargetClick(target.dataset.tabTarget);
    };
    host.addEventListener('click', handleClick);

    return () => {
      shadowRoot.adoptedStyleSheets = shadowRoot.adoptedStyleSheets.filter(
        (s) => s !== sheet
      );
      host.removeEventListener('click', handleClick);
    };
  }, [host, onTabTargetClick]);

  // When the active target changes, scroll the matching region into view
  const hasInitialisedScrollRef = useRef(false);
  useEffect(() => {
    if (!host) return;
    if (!hasInitialisedScrollRef.current) {
      hasInitialisedScrollRef.current = true;
      return;
    }
    if (!activeTarget) return;
    const region = host.shadowRoot?.querySelector(
      `[data-tab-target="${CSS.escape(activeTarget)}"]`
    );
    if (!region) return;

    region.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [activeTarget, host]);

  return hostRef;
};

export default useLivePreview;
